/** Error returned by the Kontaktfix API. `code` is a stable machine-readable string. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly issues: { path: string; message: string }[] = [],
    /** Values for the error message, e.g. { rows: 31204, max: 25000 }. */
    public readonly details: Record<string, string | number> = {},
  ) {
    super(code);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { "x-kontaktfix": "1" };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const response = await fetch(path, { method, headers, body: payload, credentials: "same-origin" });
  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as {
      error?: string;
      issues?: { path: string; message: string }[];
      details?: Record<string, string | number>;
    };
    // Maintenance mode: every customer screen is replaced by the maintenance page.
    if (
      response.status === 503 &&
      data.error === "maintenance" &&
      window.location.pathname !== "/maintenance"
    ) {
      window.location.assign("/maintenance");
    }
    throw new ApiError(response.status, data.error ?? "unknown_error", data.issues ?? [], data.details ?? {});
  }
  if (response.status === 204) return undefined as T;
  const type = response.headers.get("content-type") ?? "";
  return (type.includes("application/json") ? response.json() : response.text()) as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

export function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== "") search.set(k, String(v));
  const s = search.toString();
  return s ? `?${s}` : "";
}
